import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from '@tanstack/react-router';
import { FiLock, FiEye, FiEyeOff, FiSave, FiSend, FiUser } from 'react-icons/fi';
import { toast } from '../../utils/toast';
import { useChangePasswordMutation, useSendOtpMutation } from '../../api/auth';
import { Header } from '../layout/Header';
import { BottomNav } from '../layout/BottomNav';
import { useAuth } from '../../hooks/useAuth';

const checkPasswordStrength = (pw: string) => ({
  isLengthValid: pw.length >= 8 && pw.length <= 20,
  hasUppercase: /[A-Z]/.test(pw),
  hasNumber: /\d/.test(pw),
  hasSpecialChar: /[!#$%&()*+,\-./:;<=>?@[\\\]^_`{|}~]/.test(pw),
});

// 10 minutes – matches mobile and VerifyEmailPage
const TIMER_INITIAL = 10 * 60;

const formatTime = (seconds: number): string => {
  const m = Math.floor(seconds / 60)
    .toString()
    .padStart(2, '0');
  const s = (seconds % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
};

type Step = 'request' | 'verify';

export const ChangePasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState<Step>('request');

  // Form State
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  // Password strength
  const pwStrength = checkPasswordStrength(newPassword);
  const passwordIsValid =
    pwStrength.isLengthValid &&
    pwStrength.hasUppercase &&
    pwStrength.hasNumber &&
    pwStrength.hasSpecialChar;

  // OTP State
  const [otp, setOtp] = useState(['', '', '', '', '']);
  const [timeLeft, setTimeLeft] = useState(TIMER_INITIAL);
  const [timerKey, setTimerKey] = useState(0);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const { mutate: changePassword, isPending: isChanging } = useChangePasswordMutation();
  const { mutate: sendOtp, isPending: isSendingOtp } = useSendOtpMutation();

  // Timer effect for OTP step
  useEffect(() => {
    if (step !== 'verify') return;

    setTimeLeft(TIMER_INITIAL);
    const interval = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [step, timerKey]);

  useEffect(() => {
    if (step === 'verify') {
      inputRefs.current[0]?.focus();
    }
  }, [step]);

  const handleSendOtp = () => {
    if (!user?.email) return;

    sendOtp(
      { email: user.email, type: 'password' },
      {
        onSuccess: () => {
          setStep('verify');
        },
      },
    );
  };

  const handleOtpChange = (index: number, value: string) => {
    if (!/^\d?$/.test(value)) return;
    const digit = value.slice(-1);
    const newOtp = [...otp];
    newOtp[index] = digit;
    setOtp(newOtp);

    if (digit && index < 4) inputRefs.current[index + 1]?.focus();
    if (digit && index === 4 && newOtp.join('').length === 5) {
      toast.success('Verification code applied');
    }
  };

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && otp[index] === '' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 5);
    if (!pasted) return;
    const newOtp = ['', '', '', '', ''];
    pasted.split('').forEach((c, i) => {
      if (i < 5) newOtp[i] = c;
    });
    setOtp(newOtp);
    if (pasted.length === 5) {
      toast.success('Verification code applied');
    } else {
      inputRefs.current[pasted.length]?.focus();
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!user?.email) {
      toast.error('Session error: Email not found. Please sign in again.');
      return;
    }
    if (!currentPassword) {
      toast.error('Please enter your current password');
      return;
    }
    if (newPassword && !passwordIsValid) {
      toast.error('New password does not meet requirements.');
      setHasSubmitted(true);
      return;
    }
    if (newPassword === currentPassword) {
      toast.error('New password cannot be the same as the current password.');
      setHasSubmitted(true);
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match.');
      setHasSubmitted(true);
      return;
    }

    const fullOtp = otp.join('');
    if (fullOtp.length < 5) {
      toast.error('Please complete the verification code first');
      return;
    }

    changePassword(
      {
        email: user.email,
        otp: fullOtp,
        old_password: currentPassword,
        new_password: newPassword,
      },
      {
        onSuccess: () => {
          // Success toast is handled in the mutation onSuccess
          navigate({ to: '/profile' });
        },
        onError: () => {
          // Error is handled in the mutation onError
        },
      },
    );
  };

  return (
    <div className="min-h-screen bg-black text-white pb-32">
      <Header />

      <main className="pt-24 md:pt-20 max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumbs */}
        <nav className="flex items-center gap-2 mb-10 px-1 text-zinc-500 text-[12px] font-medium tracking-wide">
          <Link
            to="/profile"
            className="flex items-center gap-1.5 hover:text-white transition-all group/crumb"
          >
            <FiUser
              size={13}
              className="text-blue-500/80 group-hover/crumb:text-blue-400 transition-colors"
            />
            <span>Profile</span>
          </Link>
          <span className="text-zinc-800 font-light translate-y-[0.5px]">/</span>
          <span className="text-zinc-200">Change Password</span>
        </nav>

        {/* Dynamic Header */}
        <div className="mb-10">
          <h1 className="text-white font-bold text-[28px] mb-2">
            {step === 'request' && 'Request Password Change'}
            {step === 'verify' && 'Verify & Update Password'}
          </h1>
          <p className="text-zinc-500 text-[14px]">
            {step === 'request' &&
              'To ensure your account is secure, we need to send a verification code to your email.'}
            {step === 'verify' &&
              `We've sent a 5-digit code to ${user?.email || 'your email'}. Enter it below along with your current and new password.`}
          </p>
        </div>

        {/* Step Content */}
        <div className="bg-zinc-900/30 backdrop-blur-xl border border-white/5 rounded-3xl p-8 shadow-2xl">
          {step === 'request' && (
            <div className="flex flex-col gap-8 text-center py-4">
              <div className="mx-auto w-16 h-16 bg-blue-500/10 rounded-2xl flex items-center justify-center text-blue-500 border border-blue-500/20">
                <FiLock size={32} />
              </div>
              <div>
                <p className="text-zinc-400 text-sm mb-1 uppercase tracking-widest font-semibold">
                  Verified Email
                </p>
                <p className="text-white text-lg font-medium">{user?.email}</p>
              </div>
              <button
                onClick={handleSendOtp}
                disabled={isSendingOtp}
                className="w-full h-14 bg-white text-black font-bold rounded-2xl flex items-center justify-center gap-2 hover:bg-zinc-200 transition-all disabled:opacity-50 cursor-pointer"
              >
                {isSendingOtp ? (
                  'Sending...'
                ) : (
                  <>
                    <FiSend size={18} />
                    Send Verification Code
                  </>
                )}
              </button>
            </div>
          )}

          {step === 'verify' && (
            <form onSubmit={handleSubmit} className="flex flex-col gap-8">
              {/* OTP Section at the top */}
              <div className="flex flex-col items-center gap-6 py-2">
                <p className="text-zinc-400 text-[13px] font-medium uppercase tracking-wider">
                  Verification Code
                </p>
                <div className="flex gap-4 justify-center">
                  {otp.map((digit, index) => (
                    <input
                      key={index}
                      ref={(el) => {
                        inputRefs.current[index] = el;
                      }}
                      type="text"
                      inputMode="numeric"
                      value={digit}
                      onChange={(e) => handleOtpChange(index, e.target.value)}
                      onKeyDown={(e) => handleOtpKeyDown(index, e)}
                      onPaste={handleOtpPaste}
                      maxLength={1}
                      autoFocus={index === 0}
                      className="w-12 h-12 md:w-14 md:h-14 bg-zinc-900/60 border border-white/10 rounded-2xl text-center text-xl font-bold text-white focus:outline-none focus:border-blue-500 transition-all shadow-inner"
                    />
                  ))}
                </div>

                <div className="flex flex-col items-center gap-2">
                  {timeLeft > 0 ? (
                    <p className="text-blue-500 text-sm font-medium">
                      Resend in {formatTime(timeLeft)}
                    </p>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setTimerKey((k) => k + 1)}
                      className="text-blue-500 hover:text-blue-400 text-sm font-bold transition-colors"
                    >
                      Resend Code
                    </button>
                  )}
                </div>
              </div>

              <div className="h-px bg-white/5 w-full" />

              {/* Password Fields */}
              <PasswordField
                id="current-password"
                label="Current Password"
                placeholder="Enter current password"
                value={currentPassword}
                onChange={setCurrentPassword}
                show={showCurrent}
                onToggle={() => setShowCurrent((p) => !p)}
              />

              <div className="flex flex-col gap-2">
                <PasswordField
                  id="new-password"
                  label="New Password"
                  placeholder="Enter at least 8 characters"
                  value={newPassword}
                  onChange={setNewPassword}
                  show={showNew}
                  onToggle={() => setShowNew((p) => !p)}
                />
                {newPassword.length > 0 && (
                  <div className="flex flex-wrap gap-x-3 gap-y-1 ml-1 mt-0.5">
                    {[
                      { label: '8–20 chars', ok: pwStrength.isLengthValid },
                      { label: 'Uppercase', ok: pwStrength.hasUppercase },
                      { label: 'Number', ok: pwStrength.hasNumber },
                      { label: 'Special char', ok: pwStrength.hasSpecialChar },
                    ].map(({ label, ok }) => (
                      <span
                        key={label}
                        className={`text-[11px] font-medium flex items-center gap-1 ${
                          ok ? 'text-green-400' : hasSubmitted ? 'text-red-400' : 'text-zinc-500'
                        }`}
                      >
                        <span>{ok ? '✓' : '○'}</span> {label}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex flex-col gap-2">
                <PasswordField
                  id="confirm-password"
                  label="Confirm New Password"
                  placeholder="Re-type your new password"
                  value={confirmPassword}
                  onChange={setConfirmPassword}
                  show={showConfirm}
                  onToggle={() => setShowConfirm((p) => !p)}
                />
                {confirmPassword.length > 0 && (
                  <div className="ml-1 mt-0.5">
                    <span
                      className={`text-[11px] font-medium flex items-center gap-1 ${
                        newPassword === confirmPassword
                          ? 'text-green-400'
                          : hasSubmitted
                            ? 'text-red-400'
                            : 'text-zinc-500'
                      }`}
                    >
                      <span>{newPassword === confirmPassword ? '✓' : '○'}</span> Passwords match
                    </span>
                  </div>
                )}
              </div>

              <button
                type="submit"
                disabled={isChanging}
                className="mt-4 h-14 bg-white text-black font-bold rounded-2xl flex items-center justify-center gap-2 hover:bg-zinc-200 transition-all disabled:opacity-50 cursor-pointer"
              >
                <FiSave size={18} />
                {isChanging ? 'Updating...' : 'Update Password'}
              </button>

              <button
                type="button"
                onClick={() => setStep('request')}
                className="text-zinc-500 hover:text-zinc-300 text-sm font-medium transition-colors cursor-pointer"
              >
                Back to Request
              </button>
            </form>
          )}
        </div>
      </main>

      <BottomNav />
    </div>
  );
};

// ─── Sub-component: Password Field ───────────────────────────────────────────

interface PasswordFieldProps {
  id: string;
  label: string;
  placeholder: string;
  value: string;
  onChange: (v: string) => void;
  show: boolean;
  onToggle: () => void;
}

function PasswordField({
  id,
  label,
  placeholder,
  value,
  onChange,
  show,
  onToggle,
}: PasswordFieldProps) {
  return (
    <div className="flex flex-col gap-2.5">
      <label htmlFor={id} className="text-zinc-400 text-[13px] font-medium ml-1">
        {label}
      </label>
      <div className="relative flex items-center group">
        <div className="absolute left-4 text-zinc-500 group-focus-within:text-blue-500 transition-colors">
          <FiLock size={16} />
        </div>
        <input
          id={id}
          type={show ? 'text' : 'password'}
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full bg-zinc-900/60 border border-white/8 rounded-2xl py-4 pl-11 pr-12 text-white text-[14px] placeholder:text-zinc-600 focus:outline-none focus:ring-0 focus:border-blue-500/50 transition-all shadow-inner"
        />
        <button
          type="button"
          onClick={onToggle}
          className="absolute right-4 text-zinc-500 hover:text-white transition-colors"
          tabIndex={-1}
        >
          {show ? <FiEyeOff size={17} /> : <FiEye size={17} />}
        </button>
      </div>
    </div>
  );
}
